/**
 * sb-gp-product — behaviour of the GemPages product nodes rebuilt as sb-gp-* snippets (the source's GemPages and
 * Kaching runtime is app code and is not used; this is a small native rewrite on the captured markup).
 *  • Product scope = the closest <gp-product> (else the section). Its data: script[data-sb-gp-json] (variants, money).
 *  • Variant picker (sb-gp-variants): a pick updates ?variant=, the form's id, prices (sb-gp-price / sb-gp-badge),
 *    the gallery (variant image), the ADD TO CART / SOLD OUT pair and the deal block's unit selects.
 *  • Gallery (sb-gp-product-images): main slider (arrows, swipe / drag, loop) + thumbnails (click, strip arrows),
 *    desktop thumbnail column as high as the main image, feature-only image (v3) follows the variant.
 *  • Quantity (sb-gp-quantity): − / + / typed value; ADD TO CART sends it.
 *  • ADD TO CART (sb-gp-atc): /cart/add.js, then Horizon's CartLinesUpdateEvent (cart drawer opens and re-renders,
 *    bubble updates). With a deal block (sb-gp-kaching) the selected tier is added instead: its lines + the tier's
 *    discount codes through /cart/update.js, so the cart charges exactly what the tier shows (max 5 codes; a tier that
 *    can't join the cart at its price is refused with a notice, never charged differently). The tier's lines carry
 *    what each code needs (`_sb_deal` "codes"): assets/sb-gp-cart-sync.js takes a code off the cart while the cart
 *    lacks it (drawer − / removals) and puts it back once it holds it again.
 *  • Sticky bar (sb-gp-sticky-atc, #stickyAtc): shown only while the page's ADD TO CART button is off screen (above)
 *    and the footer isn't on screen; mirrors variant, tier and prices; its button runs the same add.
 *  • ADD TO CART / SOLD OUT follow the product that is actually added: the deal's units while a deal block shows
 *    (every tier sold out = SOLD OUT), else the page's own product when sb-gp-kaching handed it over
 *    (script[data-sb-gp-target]: a page product without a deal inside another product's node; sold out = the ADD TO
 *    CART button greyed out, as the source showed it), else the node's variant.
 *  • Dropdown options (select[data-sb-gp-option-select]) and the picked / not-picked option box styles
 *    (data-sb-on-style / data-sb-off-style on the option group) follow the variant.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const MAX_CODES = 5;
const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const currency = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
let moneyFmt = '${{amount}}';
/** like the snippets' money_without_trailing_zeros */
const money = (cents) =>
  formatMoney(Math.max(0, Math.round(cents)), moneyFmt, currency())
    .replace(/<[^>]*>/g, '') // an HTML money format (<span class=money>…</span>): the text goes in a node of its own
    .replace(/([.,])00(?=\D*$)/, '');
const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;

// ------------------------------------------------------------------ cart helpers (shared with the deal block)
let cartState = null;
let cartRequest = null;
function readCart() {
  if (!cartRequest) {
    cartRequest = fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (cart && Array.isArray(cart.items)) cartState = cart;
        return cart && Array.isArray(cart.items) ? cart : null;
      })
      .catch(() => null)
      .finally(() => {
        cartRequest = null;
      });
  }
  return cartRequest;
}
for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
  document.addEventListener(name, (event) => {
    const promise = /** @type {any} */ (event).promise;
    (promise ? promise.catch(() => null) : Promise.resolve()).then(() => readCart());
  });
}
const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')].map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId).filter(Boolean);
const post = (path, body) =>
  fetch(`${routeRoot()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());

/**
 * Adds items (and, when given, sets the cart's discount codes) and tells Horizon: the cart drawer opens.
 * @param {HTMLElement} source - element the events are dispatched from
 * @param {{ id: number, quantity: number, properties?: object, parent_id?: number }[]} items
 * @param {string[] | null} discount - the cart's full code list after this add (null = leave codes alone)
 * @param {string} productId
 */
async function addToCart(source, items, discount, productId) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
      promise: deferred.promise,
    })
  );
  const sections = sectionIds().join(',');
  try {
    const added = await post('cart/add.js', { items, sections: discount ? undefined : sections });
    if (added && added.status) throw Object.assign(new Error(added.description || added.message || 'Cannot add product to cart'), { ajax: added });
    let response = added;
    if (discount) response = await post('cart/update.js', { discount: discount.join(','), sections });
    const cart = discount && response && response.token ? response : await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    cartState = cart;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'product-form-component',
        sourceId: source.id || 'sb-gp-product',
        itemCount: items.reduce((s, it) => s + it.quantity, 0),
        productId: String(productId),
        sections: response && response.sections,
        didError: false,
      },
    });
    return { ok: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    source.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID', detail: { description: message } }));
    const cart = await fetch(`${routeRoot()}cart.js`).then((r) => r.json()).catch(() => ({ items: [] }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: true, items: cart.items, source: 'product-form-component', sourceId: source.id || 'sb-gp-product' },
    });
    return { ok: false, message };
  }
}

// ------------------------------------------------------------------ deal price rules (Kaching formula, as sb-deal-price)
/** @param {{type:string,value:number}|null} rule @param {number[]} units unit prices in cents */
function ruleTotal(rule, units) {
  const list = units.reduce((s, u) => s + u, 0);
  const r = rule || { type: 'default', value: 0 };
  switch (r.type) {
    case 'specific':
      return Math.min(Math.round(r.value * 100), list);
    case 'percentage':
      return units.reduce((s, u) => s + Math.ceil(((100 - r.value) * u) / 100), 0);
    case 'amount':
      return Math.max(0, list - Math.round(r.value * 100) * units.length);
    case 'pct':
      return list - Math.round(list * r.value);
    default:
      return list;
  }
}

// ------------------------------------------------------------------ pre-order notice
// The source's page script on the Cyber Monday / Black Friday 6-gifts templates: the "Pre-Order: Charcoal Roller
// Edition" box (.charcoal-preorder) shows only while Charcoal / Roller Edition is the page's variant. The other
// templates hide it with their own CSS (their copy of that script is commented out), so they are left alone.
const PREORDER_SECTIONS = '.gps-595183026049909682, .gps-595615420305506914, .gps-596427045560124006';
function syncPreorder(v) {
  const el = /** @type {HTMLElement | null} */ (document.querySelector('.charcoal-preorder'));
  if (!el || !el.closest(PREORDER_SECTIONS)) return;
  el.style.display = v && v.options[0] === 'Charcoal' && v.options[1] === 'Roller Edition' ? 'grid' : 'none';
}

// ------------------------------------------------------------------ product scope
/** @type {WeakMap<Element, Scope>} */
const scopes = new WeakMap();

class Scope {
  /** @param {Element} el */
  constructor(el) {
    this.el = el;
    const json = el.querySelector('script[data-sb-gp-json]');
    try {
      this.data = JSON.parse(json ? json.textContent || '{}' : '{}');
    } catch (e) {
      this.data = {};
    }
    if (this.data.moneyFormat) moneyFmt = this.data.moneyFormat;
    this.variants = this.data.variants || [];
    // the page's own product when the node shows another one and the page has no deal (sb-gp-kaching)
    const tgt = [...el.querySelectorAll('script[data-sb-gp-target]')].find((t) => owner(t) === el);
    try {
      this.target = tgt ? JSON.parse(tgt.textContent || 'null') : null;
    } catch (e) {
      this.target = null;
    }
    // the node shows another product than the page's (sb-gp-kaching): the source's 42px muted ADD TO CART
    this.otherPage = [...el.querySelectorAll('[data-sb-gp-other-page]')].some((t) => owner(t) === el);
    const initial = Number(this.data.selected || 0);
    this.variant = this.variants.find((v) => v.id === initial) || this.variants.find((v) => v.available) || this.variants[0] || null;
    this.galleries = [...el.querySelectorAll('[data-sb-gp-images]')].filter((g) => owner(g) === el).map((g) => new Gallery(g, this));
    this.kaching = [...el.querySelectorAll('[data-sb-gp-kaching]')].filter((k) => owner(k) === el).map((k) => new Deal(/** @type {HTMLElement} */ (k), this));
    el.addEventListener('change', this.onChange);
    el.addEventListener('click', this.onClick);
    for (const box of el.querySelectorAll('[data-sb-gp-qty]')) this.wireQty(/** @type {HTMLElement} */ (box));
    this.syncOptionStates();
    this.updateAtcLabels();
    if (this.main && this.variant) syncPreorder(this.variant);
  }

  get main() {
    return Boolean(this.data.main);
  }

  /** the GemPages quantity box value (1 when there is none) */
  get quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.el.querySelector('[data-sb-gp-qty-input]'));
    const q = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  wireQty(box) {
    const input = /** @type {HTMLInputElement | null} */ (box.querySelector('[data-sb-gp-qty-input]'));
    if (!input) return;
    const set = (n) => {
      input.value = String(Math.max(1, Math.min(9999, n)));
      box.querySelector('[data-sb-gp-qty-minus]')?.toggleAttribute('disabled', Number(input.value) <= 1);
    };
    box.querySelector('[data-sb-gp-qty-minus]')?.addEventListener('click', () => set(this.quantity - 1));
    box.querySelector('[data-sb-gp-qty-plus]')?.addEventListener('click', () => set(this.quantity + 1));
    input.addEventListener('change', () => set(this.quantity));
    set(this.quantity);
  }

  onChange = (event) => {
    const t = /** @type {HTMLInputElement} */ (event.target);
    if (t.matches('[data-sb-gp-variants] input[type="radio"]')) {
      const picked = this.selectedOptions();
      const v = this.findVariant(picked, Number(t.closest('[data-sb-gp-option]')?.getAttribute('data-sb-gp-option') || 0) - 1);
      if (v) this.setVariant(v, 'picker');
    } else if (t.matches('select[data-sb-gp-option-select]')) {
      const picked = this.selectedOptions();
      const v = this.findVariant(picked, Number(t.closest('[data-sb-gp-option]')?.getAttribute('data-sb-gp-option') || 0) - 1);
      if (v) this.setVariant(v, 'picker');
    } else if (t.matches('select[data-sb-gp-variant-select]')) {
      const v = this.variants.find((x) => String(x.id) === /** @type {HTMLSelectElement} */ (/** @type {unknown} */ (t)).value);
      if (v) this.setVariant(v, 'picker');
    }
  };

  onClick = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);
    const btn = t.closest('[data-sb-gp-atc]');
    if (btn && owner(btn) === this.el) {
      event.preventDefault();
      event.stopPropagation();
      this.add(/** @type {HTMLElement} */ (btn));
    }
  };

  /** option values picked in the GemPages picker, by position */
  selectedOptions() {
    const out = this.variant ? [...this.variant.options] : [];
    for (const block of this.el.querySelectorAll('[data-sb-gp-option]')) {
      const pos = Number(block.getAttribute('data-sb-gp-option')) - 1;
      const checked = /** @type {HTMLInputElement | null} */ (block.querySelector('input[type="radio"]:checked'));
      const dd = /** @type {HTMLSelectElement | null} */ (block.querySelector('select[data-sb-gp-option-select]'));
      if (checked && pos >= 0) out[pos] = checked.value;
      else if (dd && pos >= 0) out[pos] = dd.value;
    }
    return out;
  }

  /** exact match, else the closest variant keeping the option just picked (like Shopify's picker) */
  findVariant(options, keep = -1) {
    const exact = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    if (exact) return exact;
    if (keep < 0) return null;
    const cands = this.variants.filter((v) => v.options[keep] === options[keep]);
    return cands.find((v) => v.available) || cands[0] || null;
  }

  /**
   * @param {any} v - variant
   * @param {string} from - 'picker' | 'deal' | 'init'
   */
  setVariant(v, from) {
    if (!v) return;
    const changed = !this.variant || this.variant.id !== v.id;
    this.variant = v;
    // picker radios + labels
    for (const block of this.el.querySelectorAll('[data-sb-gp-option]')) {
      const pos = Number(block.getAttribute('data-sb-gp-option')) - 1;
      const group = block.querySelector('[data-sb-on-style]');
      for (const input of block.querySelectorAll('input[type="radio"]')) {
        const on = /** @type {HTMLInputElement} */ (input).value === v.options[pos];
        /** @type {HTMLInputElement} */ (input).checked = on;
        const label = input.closest('label.option-item');
        label?.classList.toggle('option-item-active', on);
        const box = /** @type {HTMLElement | null | undefined} */ (label?.querySelector('.option-value-wrapper'));
        const st = group && group.getAttribute(on ? 'data-sb-on-style' : 'data-sb-off-style');
        if (box && st) box.setAttribute('style', st);
      }
      const dd = /** @type {HTMLSelectElement | null} */ (block.querySelector('select[data-sb-gp-option-select]'));
      if (dd && v.options[pos] != null) dd.value = v.options[pos];
      const label = block.querySelector('[data-sb-gp-opt-label]');
      if (label) label.textContent = v.options[pos] || '';
    }
    this.syncOptionStates();
    // product form id, the all-variants dropdown
    for (const input of this.el.querySelectorAll('form input[name="id"]')) /** @type {HTMLInputElement} */ (input).value = String(v.id);
    for (const sel of this.el.querySelectorAll('select[data-sb-gp-variant-select]')) {
      if (owner(sel) === this.el) /** @type {HTMLSelectElement} */ (sel).value = String(v.id);
    }
    // prices
    for (const p of this.el.querySelectorAll('[data-sb-gp-price]')) {
      if (owner(p) !== this.el) continue;
      const type = p.getAttribute('data-sb-gp-price');
      const m = p.querySelector('.money') || p.querySelector('[data-gp-text]');
      if (type === 'compare') {
        if (m) m.textContent = money(v.compare_at_price || 0);
        p.setAttribute('data-hidden', String(!(v.compare_at_price > v.price)));
      } else if (m) m.textContent = money(v.price);
    }
    for (const b of this.el.querySelectorAll('[data-sb-gp-badge]')) {
      if (owner(b) !== this.el) continue;
      const tpl = b.getAttribute('data-sb-gp-badge') || '';
      const off = (v.compare_at_price || 0) - v.price;
      const pre = b.querySelector('pre, #product-badge-content');
      if (pre && off > 0) {
        pre.innerHTML = '';
        const parts = tpl.split(/(\{amount_discount\}|\{percent_discount\})/);
        for (const part of parts) {
          if (part === '{amount_discount}') {
            const s = document.createElement('span');
            s.className = 'money';
            s.textContent = money(off);
            pre.append(s);
          } else if (part === '{percent_discount}') pre.append(`${Math.round((off * 100) / v.compare_at_price)}%`);
          else pre.append(part);
        }
      }
      b.setAttribute('data-hidden', String(!(off > 0)));
      b.setAttribute('gp-data-hidden', String(!(off > 0)));
    }
    // gallery
    if (changed && from !== 'init') for (const g of this.galleries) g.showMedia(v.featured_media_id, v);
    // URL (the page's own product only)
    if (this.main && changed && from !== 'init') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(v.id));
      window.history.replaceState(window.history.state, '', url.toString());
    }
    if (from !== 'deal') for (const d of this.kaching) d.followVariant(v);
    if (this.main) syncPreorder(v);
    this.updateAtcLabels();
    this.el.dispatchEvent(new CustomEvent('sb-gp:variant', { bubbles: true, detail: { variant: v, scope: this } }));
  }

  /** picker values that can't make an available variant with the other picked options */
  syncOptionStates() {
    if (!this.variant) return;
    const sel = this.variant.options;
    for (const block of this.el.querySelectorAll('[data-sb-gp-option]')) {
      const pos = Number(block.getAttribute('data-sb-gp-option')) - 1;
      for (const input of block.querySelectorAll('input[type="radio"]')) {
        const val = /** @type {HTMLInputElement} */ (input).value;
        const ok = this.variants.some((v) => v.available && v.options[pos] === val && v.options.every((o, i) => i === pos || o === sel[i]));
        const wrap = input.closest('label.option-item')?.querySelector('.option-value-wrapper');
        if (wrap) {
          wrap.setAttribute('data-gp-option-available', String(ok));
          wrap.toggleAttribute('data-sb-unavailable', !ok);
        }
      }
    }
  }

  /** the page product's variant matching the picked options (data-sb-gp-target), or its only variant */
  targetVariant(v) {
    const t = this.target;
    if (!t || !Array.isArray(t.variants) || !t.variants.length) return null;
    if (t.variants.length === 1) return t.variants[0];
    return (v && t.variants.find((x) => x.options.every((o, i) => o === v.options[i]))) || null;
  }

  /** can the product that is actually added be bought right now (deal units / page product / node variant) */
  available(v = this.variant) {
    const deal = this.activeDeal();
    if (deal) return deal.config.available !== false && !(deal.tier && deal.tier.soldOut) && deal.unitVariants(deal.selected).every((x) => x && x.available);
    if (this.target) {
      const tv = this.targetVariant(v);
      return Boolean(tv && tv.available);
    }
    return Boolean(v && v.available);
  }

  /** ADD TO CART / SOLD OUT pair (+ the owner's button look) for the product that is actually added */
  syncAvailability() {
    const ok = this.available(this.variant);
    // a page product without a deal (target): the source greyed its ADD TO CART instead of showing SOLD OUT
    const grey = !ok && Boolean(this.target);
    for (const w of this.el.querySelectorAll('[data-sb-gp-atc-btn-wrap]')) if (owner(w) === this.el) w.classList.toggle('!gp-hidden', !ok && !grey);
    for (const w of this.el.querySelectorAll('[data-sb-gp-soldout-wrap]')) if (owner(w) === this.el) w.classList.toggle('!gp-hidden', ok || grey);
    for (const b of this.el.querySelectorAll('[data-sb-gp-atc]')) {
      if (owner(b) !== this.el) continue;
      b.toggleAttribute('disabled', grey);
      b.toggleAttribute('data-sb-gp-target-atc', this.otherPage); // the source's 42px muted button (sb-gp-product.css)
    }
    for (const b of this.el.querySelectorAll('.cm-atc-btn')) if (owner(b) === this.el) b.setAttribute('data-atc-sold-out', String(!ok));
  }

  /** the selected deal tier (visible deal block only) */
  activeDeal() {
    return this.kaching.find((d) => d.visible && d.tier) || null;
  }

  /** "ADD TO CART - $old $new" of the owner's button look + the sticky bar */
  updateAtcLabels() {
    this.syncAvailability();
    const deal = this.activeDeal();
    const v = this.variant;
    let price = null;
    let compare = null;
    if (deal) {
      const t = deal.totals(deal.selected);
      price = t.price;
      compare = t.compare;
    }
    for (const b of this.el.querySelectorAll('.cm-atc-btn')) {
      if (owner(b) !== this.el) continue;
      const oldEl = b.querySelector('[data-sb-gp-atc-old]');
      const newEl = b.querySelector('[data-sb-gp-atc-new]');
      if (deal && price != null) {
        b.setAttribute('data-atc-state', `tier_${deal.selected + 1}`);
        if (oldEl) oldEl.textContent = compare > price ? money(compare) : '';
        if (newEl) newEl.textContent = money(price);
      } else {
        b.setAttribute('data-atc-state', 'none');
        if (oldEl) oldEl.textContent = '';
        if (newEl) newEl.textContent = '';
      }
    }
    // the GemPages price inside the add button: the selected tier's totals (Kaching), else the variant's
    for (const p of this.el.querySelectorAll('[data-sb-gp-atc] [data-sb-gp-atc-price]')) {
      if (owner(p) !== this.el || !v) continue;
      const cmp = p.getAttribute('data-sb-gp-atc-price') === 'compare';
      const pr = deal && price != null ? price : v.price;
      const cp = deal && price != null ? compare : v.compare_at_price || 0;
      const m = p.querySelector('.money') || p.querySelector('[data-gp-text]');
      if (m) m.textContent = money(cmp ? cp : pr);
      if (cmp) p.setAttribute('data-hidden', String(!(cp > pr)));
    }
    sticky?.update();
    if (v && !deal) for (const n of this.el.querySelectorAll('[data-sb-gp-atc-total]')) n.textContent = money(v.price * this.quantity);
  }

  /** @param {HTMLElement} button */
  async add(button) {
    if (this.busy) return;
    const v = this.variant;
    if (!v || !this.available(v)) return;
    this.busy = true;
    button.setAttribute('data-state', 'loading');
    this.notice(button, '');
    try {
      const deal = this.activeDeal();
      if (deal) {
        const r = await deal.add();
        if (r && !r.ok && r.message) this.notice(button, r.message);
      } else {
        const tv = this.target ? this.targetVariant(v) : null;
        const r = await addToCart(button, [{ id: tv ? tv.id : v.id, quantity: this.quantity }], null, tv ? this.target.productId : this.data.id);
        if (!r.ok) this.notice(button, r.message);
      }
    } finally {
      this.busy = false;
      button.setAttribute('data-state', 'idle');
    }
  }

  /** small error line under the button (GemPages shows its error message there) */
  notice(button, text) {
    const wrap = button.closest('gp-product-button') || button.parentElement;
    if (!wrap) return;
    let el = /** @type {HTMLElement | null} */ (wrap.querySelector('[data-sb-gp-error]'));
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-sb-gp-error', '');
      el.setAttribute('role', 'alert');
      el.className = 'sb-gp-error';
      wrap.append(el);
    }
    el.textContent = text || '';
  }
}

/** the product scope element an element belongs to */
function owner(el) {
  return el.closest('gp-product') || el.closest('[data-sb-gp-scope]') || el.closest('.shopify-section') || document.body;
}
function scopeOf(el) {
  const o = owner(el);
  let s = scopes.get(o);
  if (!s && o.querySelector('script[data-sb-gp-json]')) {
    s = new Scope(o);
    scopes.set(o, s);
  }
  return s;
}

// ------------------------------------------------------------------ gallery
class Gallery {
  /** @param {Element} el @param {Scope} scope */
  constructor(el, scope) {
    this.el = el;
    this.scope = scope;
    this.slider = /** @type {HTMLElement | null} */ (el.querySelector('[data-sb-gp-slides]'));
    this.slides = this.slider ? /** @type {HTMLElement[]} */ ([...this.slider.children].filter((c) => c.classList.contains('gem-slider-item'))) : [];
    this.thumbsBox = /** @type {HTMLElement | null} */ (el.querySelector('[data-sb-gp-thumbs]'));
    this.thumbs = this.thumbsBox ? /** @type {HTMLElement[]} */ ([...this.thumbsBox.children].filter((c) => c.classList.contains('gem-slider-item'))) : [];
    this.feature = /** @type {HTMLElement | null} */ (el.querySelector('[data-sb-gp-feature]'));
    this.index = 0;
    if (this.slider) {
      this.slider.classList.add('sb-gp-slider');
      const main = this.slider.closest('gp-carousel');
      main?.querySelector('.gp-carousel-action-back')?.addEventListener('click', (e) => (e.preventDefault(), this.go(this.index - 1)));
      main?.querySelector('.gp-carousel-action-next')?.addEventListener('click', (e) => (e.preventDefault(), this.go(this.index + 1)));
      this.wireSwipe();
    }
    if (this.thumbsBox) {
      this.thumbsBox.classList.add('sb-gp-thumbs');
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      const car = this.thumbsBox.closest('gp-carousel');
      car?.querySelector('.gp-carousel-action-back')?.addEventListener('click', (e) => (e.preventDefault(), this.go(this.index - 1)));
      car?.querySelector('.gp-carousel-action-next')?.addEventListener('click', (e) => (e.preventDefault(), this.go(this.index + 1)));
    }
    this.fitThumbColumn();
    if ('ResizeObserver' in window && this.slider) new ResizeObserver(() => this.fitThumbColumn()).observe(this.slider);
    window.addEventListener('resize', () => this.fitThumbColumn());
    // the selected variant's image first, only when the URL names it (?variant=): a plain product URL opens on the
    // featured image, as the source did (The Black Bag 8.jpg, not variant 1's 4.jpg)
    const v = scope.variant;
    const urlVariant = new URLSearchParams(location.search).get('variant');
    if (v && v.featured_media_id && urlVariant && urlVariant === String(v.id)) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(v.featured_media_id));
      if (i > 0) this.go(i, false);
    }
    this.el.setAttribute('data-sb-ready', '');
  }

  /** desktop: the thumbnail column is as high as the main image (GemPages sets it at run time) */
  fitThumbColumn() {
    const wrap = /** @type {HTMLElement | null} */ (this.el.querySelector('.gallery-wrapper'));
    if (!wrap || !this.slider) return;
    // only a two-column gallery (thumbs beside the slider) takes the slider's height; a stacked one (CraveLess) keeps
    // its square thumb row
    const twoCols = wrap.parentElement && getComputedStyle(wrap.parentElement).gridTemplateColumns.trim().split(/\s+/).length > 1;
    if (isDesktop() && twoCols) {
      const h = this.slider.getBoundingClientRect().height;
      if (h > 0) {
        wrap.style.height = `${Math.round(h)}px`;
        wrap.style.display = 'flex';
      }
    } else {
      wrap.style.height = '';
      wrap.style.display = '';
    }
  }

  /** @param {number} i @param {boolean} [smooth] @param {boolean} [fade] */
  go(i, smooth = true, fade = false) {
    const n = this.slides.length || this.thumbs.length;
    if (!n) return;
    const from = this.index;
    this.index = ((i % n) + n) % n;
    // owner 2026-10-24: a jump of more than one photo (a far thumbnail, another variant, the wrap from the last photo
    // to the first) fades into the photo instead of sliding past every photo in between; next / previous still slide
    const far = Math.abs(this.index - from) > 1;
    const reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (smooth && (fade || far) && this.slider && this.slides.length && !reduce && this.index !== from) this.fadeTo();
    else this.place(smooth && !reduce);
    this.thumbs.forEach((t, k) => t.setAttribute('data-outline', k === this.index ? 'active' : 'deactive'));
    const t = this.thumbs[this.index];
    if (t && this.thumbsBox) {
      const box = this.thumbsBox;
      const vertical = box.scrollHeight > box.clientHeight + 2 && getComputedStyle(box).flexDirection.startsWith('column');
      if (vertical) {
        const top = t.offsetTop - box.offsetTop;
        if (top < box.scrollTop || top + t.offsetHeight > box.scrollTop + box.clientHeight) box.scrollTo({ top: top - (box.clientHeight - t.offsetHeight) / 2, behavior: smooth ? 'smooth' : 'auto' });
      } else {
        const left = t.offsetLeft - box.offsetLeft;
        if (left < box.scrollLeft || left + t.offsetWidth > box.scrollLeft + box.clientWidth) box.scrollTo({ left: left - (box.clientWidth - t.offsetWidth) / 2, behavior: smooth ? 'smooth' : 'auto' });
      }
    }
  }

  /** @param {boolean} smooth  the slides at this.index, sliding (0.45 s) or at once */
  place(smooth) {
    for (const s of this.slides) {
      s.style.transition = smooth ? 'transform 0.45s ease' : 'none';
      s.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;
    }
  }

  /** fade out, move to this.index at once, fade in (a newer jump cancels the pending one) */
  fadeTo() {
    const sl = /** @type {HTMLElement} */ (this.slider);
    const token = (this.fadeToken = (this.fadeToken || 0) + 1);
    sl.style.transition = 'opacity 0.18s ease';
    sl.style.opacity = '0';
    setTimeout(() => {
      if (token !== this.fadeToken) return;
      this.place(false);
      void sl.offsetWidth; // the jump is laid out before the fade-in starts
      sl.style.transition = 'opacity 0.25s ease';
      sl.style.opacity = '1';
    }, 180);
  }

  /** variant image: the slide holding it, or (feature-only gallery) the image itself; another variant always fades */
  showMedia(mediaId, v) {
    if (this.feature && v && v.image) {
      const img = /** @type {HTMLImageElement | null} */ (this.feature.querySelector('img'));
      if (img && img.getAttribute('src') !== v.image) {
        const swap = () => { img.src = v.image; img.removeAttribute('srcset'); };
        if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) { swap(); return; }
        img.style.transition = 'opacity 0.18s ease';
        img.style.opacity = '0';
        setTimeout(() => { swap(); img.style.transition = 'opacity 0.25s ease'; img.style.opacity = '1'; }, 180);
      }
      return;
    }
    if (!mediaId) return;
    const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (i >= 0) this.go(i, true, true);
  }

  wireSwipe() {
    const slider = /** @type {HTMLElement} */ (this.slider);
    let x0 = 0;
    let y0 = 0;
    let dx = 0;
    let active = false;
    let horizontal = null;
    const width = () => slider.getBoundingClientRect().width || 1;
    slider.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      active = true;
      horizontal = null;
      x0 = e.clientX;
      y0 = e.clientY;
      dx = 0;
    });
    slider.addEventListener('pointermove', (e) => {
      if (!active) return;
      const mx = e.clientX - x0;
      const my = e.clientY - y0;
      if (horizontal === null && (Math.abs(mx) > 6 || Math.abs(my) > 6)) horizontal = Math.abs(mx) > Math.abs(my);
      if (!horizontal) return;
      dx = mx;
      try {
        slider.setPointerCapture(e.pointerId);
      } catch (err) {
        /* already released */
      }
      const pct = (dx / width()) * 100;
      for (const s of this.slides) {
        s.style.transition = 'none';
        s.style.transform = `translate3d(calc(${-this.index * 100}% + ${pct}%), 0, 0)`;
      }
    });
    const end = () => {
      if (!active) return;
      active = false;
      if (horizontal && Math.abs(dx) > Math.min(60, width() * 0.15)) this.go(this.index + (dx < 0 ? 1 : -1));
      else if (horizontal) this.go(this.index);
      if (horizontal) {
        // a drag is not a click on the image
        const stop = (ev) => (ev.stopPropagation(), ev.preventDefault());
        slider.addEventListener('click', stop, { capture: true, once: true });
        setTimeout(() => slider.removeEventListener('click', stop, { capture: true }), 50);
      }
    };
    slider.addEventListener('pointerup', end);
    slider.addEventListener('pointercancel', end);
    slider.addEventListener('dragstart', (e) => e.preventDefault());
  }
}

// ------------------------------------------------------------------ deal block (Kaching look, native codes)
class Deal {
  /** @param {HTMLElement} el @param {Scope} scope */
  constructor(el, scope) {
    this.el = el;
    this.scope = scope;
    const cfg = el.querySelector('script[data-sb-config]');
    try {
      this.config = JSON.parse(cfg ? cfg.textContent || '{}' : '{}');
    } catch (e) {
      this.config = { tiers: [] };
    }
    this.selected = Number(this.config.selected || 0);
    // the deal's own product (Kaching showed the PAGE product's deal, even inside another product's GemPages node)
    this.variants = this.config.variants && this.config.variants.length ? this.config.variants : scope.variants;
    // this deal's codes and what each needs, for assets/sb-gp-cart-sync.js (also a typed-in deal code is held to them)
    const w = /** @type {any} */ (window);
    w.SbGpDealCodes = w.SbGpDealCodes || {};
    for (const t of this.config.tiers || []) Object.assign(w.SbGpDealCodes, this.codeNeeds(t) || {});
    el.addEventListener('click', this.onClick);
    el.addEventListener('keydown', this.onKey);
    el.addEventListener('change', this.onChange);
    this.refreshPrices();
    if (!cartState) readCart();
    this.fitSelects();
    if ('ResizeObserver' in window) new ResizeObserver(() => this.fitSelects()).observe(el);
  }

  /** The native select sits invisibly over its option box. Where the page's GemPages CSS forces the option box to
   *  position:static (the Model option: thumbnail at the row's left, name box in the row), the select is placed
   *  over the visible name box by measurement, so it never covers the neighbouring Colour box. */
  fitSelects() {
    for (const sel of /** @type {NodeListOf<HTMLSelectElement>} */ (this.el.querySelectorAll('select.sb-kaching-select'))) {
      const box = /** @type {HTMLElement | null} */ (sel.closest('.kaching-bundles-option'));
      const op = /** @type {HTMLElement | null} */ (sel.offsetParent);
      if (!box || !op) continue;
      if (op === box) {
        if (sel.hasAttribute('data-sb-fit')) { sel.removeAttribute('data-sb-fit'); sel.style.cssText = ''; }
        continue;
      }
      const face = /** @type {HTMLElement} */ (box.querySelector('.kaching-bundles__bar-variant-select') || box);
      const a = face.getBoundingClientRect();
      const b = op.getBoundingClientRect();
      sel.setAttribute('data-sb-fit', '');
      // a name box the page hides (thumbnail-only option): no tap target, never a cover over the next box
      if (!a.width) { sel.style.cssText = 'display:none'; continue; }
      sel.style.cssText = `inset:auto;left:${a.left - b.left + op.scrollLeft - op.clientLeft}px;top:${a.top - b.top + op.scrollTop - op.clientTop}px;width:${a.width}px;height:${a.height}px`;
    }
  }

  get visible() {
    return this.el.isConnected && this.el.getClientRects().length > 0;
  }

  get bars() {
    return /** @type {HTMLElement[]} */ ([...this.el.querySelectorAll('.kaching-bundles__bar')]);
  }

  get tier() {
    return (this.config.tiers || [])[this.selected] || null;
  }

  onClick = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);
    if (t.closest('select')) return;
    const bar = t.closest('.kaching-bundles__bar');
    if (!bar) return;
    if (t.closest('label')) event.preventDefault(); // the label's radio is ours to set
    this.select(this.bars.indexOf(/** @type {HTMLElement} */ (bar)));
  };

  onKey = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);
    if ((event.key === 'Enter' || event.key === ' ') && t.classList.contains('kaching-bundles__bar-main')) {
      event.preventDefault();
      this.select(this.bars.indexOf(/** @type {HTMLElement} */ (t.closest('.kaching-bundles__bar'))));
    }
  };

  onChange = (event) => {
    const sel = /** @type {HTMLSelectElement} */ (event.target);
    if (!sel.matches('select[data-unit]')) return;
    const unit = sel.closest('[data-sb-unit]');
    if (!unit) return;
    this.syncUnit(unit);
    this.refreshPrices();
    // Kaching option sync: the first unit's variant is the page's variant
    if (unit.getAttribute('data-sb-unit') === '0') {
      const v = this.unitVariant(unit);
      const sv = v && this.scope.variants.find((x) => x.options.every((o, k) => o === v.options[k]));
      if (sv) this.scope.setVariant(sv, 'deal');
    }
    this.scope.updateAtcLabels();
    this.fitSelects();
  };

  /** @param {number} i */
  select(i) {
    const tier = (this.config.tiers || [])[i];
    if (!tier || tier.soldOut) return;
    this.selected = i;
    this.notice('');
    this.bars.forEach((b, k) => {
      b.classList.toggle('kaching-bundles__bar--selected', k === i);
      const radio = /** @type {HTMLInputElement | null} */ (b.querySelector('input[type="radio"]'));
      if (radio) radio.checked = k === i;
    });
    this.scope.updateAtcLabels();
    requestAnimationFrame(() => this.fitSelects());
  }

  /** the page's variant changed: every unit select of every tier follows (Kaching option sync) */
  followVariant(v) {
    for (const unit of this.el.querySelectorAll('[data-sb-unit]')) {
      for (const sel of unit.querySelectorAll('select[data-unit]')) {
        const pos = Number(sel.getAttribute('data-opt'));
        const val = v.options[pos];
        if ([...sel.options].some((o) => o.value === val)) /** @type {HTMLSelectElement} */ (sel).value = val;
      }
      this.syncUnit(unit);
    }
    this.refreshPrices();
    this.fitSelects();
  }

  /** a unit's variant from its option selects */
  unitVariant(unit) {
    const opts = [...this.current().options];
    for (const sel of unit.querySelectorAll('select[data-unit]')) opts[Number(sel.getAttribute('data-opt'))] = /** @type {HTMLSelectElement} */ (sel).value;
    return this.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
  }

  /** the deal product's variant matching the page variant's options (else its first available) */
  current() {
    const sv = this.scope.variant;
    const same = sv && this.variants.find((v) => v.options.every((o, i) => o === sv.options[i]));
    return same || this.variants.find((v) => v.available) || this.variants[0] || { options: [] };
  }

  /** the shown swatch / name / image of a unit follow its selects */
  syncUnit(unit) {
    const v = this.unitVariant(unit);
    for (const sel of unit.querySelectorAll('select[data-unit]')) {
      const s = /** @type {HTMLSelectElement} */ (sel);
      const box = s.closest('.kaching-bundles__swatch-dropdown');
      if (!box) continue;
      box.setAttribute('data-value', s.value);
      const name = box.querySelector('[data-sb-name]');
      if (name) name.textContent = s.value;
      const sw = /** @type {HTMLElement | null} */ (box.querySelector('.kaching-bundles__swatch'));
      const opt = /** @type {HTMLOptionElement | undefined} */ (s.selectedOptions[0]);
      if (sw && opt && opt.dataset.color) sw.style.setProperty('--kaching-swatch-color', opt.dataset.color);
      if (sw && box.hasAttribute('data-image-swatch') && v && v.image) sw.style.setProperty('--kaching-swatch-image-url', `url(${v.image})`);
    }
    // a sold-out deal product keeps its selects in full colour (only the button greys out, as the source showed)
    unit.toggleAttribute('data-sb-unavailable', this.config.available !== false && !(v && v.available));
  }

  /** variants of the selected tier's units (main product) */
  unitVariants(i) {
    const bar = this.bars[i];
    const units = bar ? [...bar.querySelectorAll('[data-sb-unit]')] : [];
    const tier = (this.config.tiers || [])[i];
    const main = tier ? tier.lines.find((l) => l.main) || tier.lines[0] : null;
    const qty = main ? main.qty : 1;
    const list = [];
    for (let k = 0; k < qty; k++) {
      const u = units[k] || units[0];
      list.push((u && this.unitVariant(u)) || this.current());
    }
    return list.filter(Boolean);
  }

  /** price and compare-at total of tier i with its units' variants (Kaching formula) */
  totals(i) {
    const tier = (this.config.tiers || [])[i];
    if (!tier) return { price: 0, compare: 0 };
    let price = 0;
    let compare = 0;
    tier.lines.forEach((l, li) => {
      let units;
      if (l.main) units = this.unitVariants(i).map((v) => ({ p: v.price, c: v.compare_at_price }));
      else units = Array.from({ length: l.qty }, () => ({ p: l.price, c: l.compare }));
      price += ruleTotal(l.rule, units.map((u) => u.p));
      compare += units.reduce((s, u) => s + (this.config.compareAt && u.c > u.p ? u.c : u.p), 0);
    });
    // theme setting sb_full_prices off: only the charged price shows (no crossed-out full price anywhere)
    if (this.config.fullPrices === false) compare = price;
    return { price, compare };
  }

  /** bar prices + "{{new_price}}" subtitles after a unit change */
  refreshPrices() {
    this.bars.forEach((bar, i) => {
      const t = this.totals(i);
      const tier = (this.config.tiers || [])[i];
      if (!tier) return;
      const units = tier.lines.filter((l) => l.main).reduce((s, l) => s + l.qty, 0) || 1;
      const pe = bar.querySelector('[data-sb-price]');
      const ce = bar.querySelector('[data-sb-compare]');
      if (pe) pe.textContent = money(t.price);
      if (ce) {
        ce.textContent = money(t.compare);
        ce.closest('.kaching-bundles__bar-full-price')?.toggleAttribute('hidden', !(t.compare > t.price));
      }
      for (const n of bar.querySelectorAll('[data-sb-each]')) n.textContent = money(t.price / units);
      for (const n of bar.querySelectorAll('[data-sb-saved-pct]')) n.textContent = `${t.compare > 0 ? Math.round(((t.compare - t.price) * 100) / t.compare) : 0}%`;
      for (const n of bar.querySelectorAll('[data-sb-saved]')) n.textContent = money(t.compare - t.price);
    });
  }

  notice(text) {
    const el = /** @type {HTMLElement | null} */ (this.el.querySelector('[data-sb-notice]'));
    if (!el) return;
    el.textContent = text || '';
    if (text) {
      const r = el.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  /** codes the cart keeps + this tier's (see sb-bundles.js planCodes; same rules) */
  planCodes(cart, codes, handles) {
    if (!cart || !Array.isArray(cart.items)) return null;
    const key = (c) => String(c).trim().toUpperCase();
    const adding = [];
    for (const c of codes) if (c && !adding.some((x) => key(x) === key(c))) adding.push(c);
    const addingKeys = new Set(adding.map(key));
    const tiers = this.config.tiers || [];
    const own = new Map();
    tiers.forEach((t, i) => {
      for (const c of t.codes || []) {
        if (!own.has(key(c))) own.set(key(c), new Set());
        own.get(key(c)).add(i + 1);
      }
    });
    const tiersInCart = new Set();
    for (const item of cart.items) {
      const raw = item && item.properties && item.properties._sb_deal;
      if (!raw) continue;
      try {
        const deal = JSON.parse(raw);
        if (deal && deal.deal === this.config.handle) tiersInCart.add(Number(deal.tier));
      } catch (e) {
        /* not ours */
      }
    }
    const keep = [];
    for (const d of cart.discount_codes || []) {
      const k = key(d.code || '');
      if (!k || addingKeys.has(k)) continue;
      const used = own.get(k);
      // a code the shopper brought (the pop-up's MYSTERY10, a link code) is kept even while it is not yet applicable
      // (empty cart): /cart/update.js replaces the whole list, so dropping it here would silently remove it
      if (d.applicable === false && used) continue;
      if (used && ![...used].some((t) => tiersInCart.has(t))) continue;
      keep.push(d.code);
    }
    const merged = [...keep, ...adding];
    const mine = new Set(handles);
    const overlap = cart.items.some((item) => item && item.properties && item.properties._sb_deal && mine.has(item.handle));
    return { merged, blocked: merged.length > MAX_CODES || overlap };
  }

  /**
   * What each of the tier's codes needs in the cart, for assets/sb-gp-cart-sync.js (it removes a code from the cart
   * while the cart lacks it and puts it back once the cart holds it again). kit/data/build-bundles.mjs makes a tier's
   * codes in this order: one per discounted tier line (products = that line, minimum = its units), then, when the tier
   * has product gifts, the gift code (main product + gifts). A line code needs ALL the tier's lines (a bundle price
   * only for the whole bundle); the gift code needs the tier's lines AND its gifts. null = no codes.
   * @returns {Record<string, Record<string, number>> | null}
   */
  codeNeeds(tier) {
    const { codes, gifts } = this.liveDeal(tier);
    if (!codes.length) return null;
    const lines = {};
    for (const l of tier.lines || []) if (l.handle) lines[l.handle] = (lines[l.handle] || 0) + Number(l.qty || 0);
    const withGifts = { ...lines };
    for (const g of gifts) withGifts[g.handle] = (withGifts[g.handle] || 0) + Number(g.qty || 0);
    const giftCode = gifts.length ? codes[codes.length - 1] : null;
    return Object.fromEntries(codes.map((c) => [c, c === giftCode ? withGifts : lines]));
  }

  /**
   * The tier's gifts and codes that can go in the cart. A gift whose product is gone from the store (no variant: the
   * owner deleted it, 2026-10-26) is left out, and so is the gift code when no gift is left: Shopify refuses a whole
   * /cart/add.js that holds an unknown variant, so a missing gift must never block the purchase of the tier itself.
   */
  liveDeal(tier) {
    const codes = (tier.codes || []).filter(Boolean);
    const all = (tier.gifts || []).filter((g) => g.handle);
    const gifts = all.filter((g) => Number(g.variant) > 0);
    return { gifts, codes: all.length && !gifts.length ? codes.slice(0, -1) : codes };
  }

  /** the selected tier → cart lines (gift / bundle lines under the main line, main line last, like sb-bundles) */
  buildCart() {
    const i = this.selected;
    const tier = this.tier;
    const needs = this.codeNeeds(tier);
    const deal = (role, gift) => ({
      _sb_deal: JSON.stringify({ deal: this.config.handle, tier: i + 1, title: tier.title, role, ...(gift == null ? {} : { gift }), ...(needs ? { codes: needs } : {}) }),
    });
    const groups = new Map();
    for (const v of this.unitVariants(i)) groups.set(v.id, (groups.get(v.id) || 0) + 1);
    const entries = [...groups.entries()];
    const parent = entries.length ? entries[0][0] : this.current().id;
    const items = [];
    const handles = new Set(tier.lines.map((l) => l.handle));
    tier.lines.forEach((l) => {
      if (l.main) return;
      items.push({ id: Number(l.variant), quantity: l.qty, properties: deal('bundle'), parent_id: parent });
    });
    const live = this.liveDeal(tier);
    for (const g of live.gifts) {
      handles.add(g.handle);
      items.push({ id: Number(g.variant), quantity: g.qty, properties: deal('gift', g.id ?? g.index), parent_id: parent });
    }
    entries.slice(1).forEach(([vid, q]) => items.push({ id: Number(vid), quantity: q, properties: deal('main') }));
    if (entries.length) items.push({ id: Number(entries[0][0]), quantity: entries[0][1], properties: deal('main') });
    return { items, codes: live.codes, handles: [...handles] };
  }

  async add() {
    const tier = this.tier;
    if (!tier) return { ok: false };
    const unavailable = this.unitVariants(this.selected).some((v) => !v.available);
    if (unavailable) {
      this.notice(this.el.dataset.unavailableMessage || '');
      return { ok: false };
    }
    const { items, codes, handles } = this.buildCart();
    const plan = this.planCodes(await readCart(), codes, handles);
    if (!plan || plan.blocked) {
      this.notice(plan ? this.el.dataset.combineMessage || '' : this.el.dataset.errorMessage || '');
      return { ok: false };
    }
    this.notice('');
    return addToCart(this.el, items, codes.length ? plan.merged : null, this.config.productId);
  }
}

// ------------------------------------------------------------------ sticky bar (the owner's #stickyAtc look)
class Sticky {
  /** @param {HTMLElement} el */
  constructor(el) {
    this.el = el;
    this.state = new Map();
    el.addEventListener('click', (e) => {
      const btn = /** @type {HTMLElement} */ (e.target).closest('#atcBtnMain, [data-sb-sticky-btn]');
      if (!btn && !(/** @type {HTMLElement} */ (e.target).closest('.sticky-atc__info'))) return;
      const s = this.scope();
      const main = this.mainButton();
      if (!s || !main) return;
      if (btn) {
        e.preventDefault();
        s.add(/** @type {HTMLElement} */ (btn));
      } else main.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    this.io = new IntersectionObserver((entries) => {
      for (const en of entries) this.state.set(en.target, en);
      this.toggle();
    });
    this.watch();
    this.update();
  }

  /** the page's main add-to-cart button (first visible one) */
  mainButton() {
    return /** @type {HTMLElement | null} */ ([...document.querySelectorAll('[data-sb-gp-atc]')].find((b) => b.getClientRects().length > 0) || null);
  }

  scope() {
    const b = this.mainButton();
    return b ? scopeOf(b) : null;
  }

  watch() {
    const b = this.mainButton();
    const wrap = b ? b.closest('gp-product-button') || b : null;
    if (wrap && wrap !== this.watched) {
      if (this.watched) this.io.unobserve(this.watched);
      this.watched = wrap;
      this.io.observe(wrap);
    }
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    if (footer && footer !== this.footer) {
      this.footer = footer;
      this.io.observe(footer);
    }
  }

  toggle() {
    const btn = this.watched ? this.state.get(this.watched) : null;
    const foot = this.footer ? this.state.get(this.footer) : null;
    // shown once the main button has scrolled away ABOVE the screen, never over it, never over the footer
    const passed = Boolean(btn && !btn.isIntersecting && btn.boundingClientRect.top < 0);
    const show = passed && !(foot && foot.isIntersecting);
    this.el.classList.toggle('is-visible', show);
    this.el.setAttribute('aria-hidden', String(!show));
    for (const b of this.el.querySelectorAll('button')) /** @type {HTMLElement} */ (b).tabIndex = show ? 0 : -1;
  }

  update() {
    this.watch();
    const s = this.scope();
    if (!s || !s.variant) return;
    const v = s.variant;
    const deal = s.activeDeal();
    const t = deal ? deal.totals(deal.selected) : { price: v.price * s.quantity, compare: (v.compare_at_price || v.price) * s.quantity };
    const set = (sel, text) => {
      const n = this.el.querySelector(sel);
      if (n) n.textContent = text;
    };
    set('#atcVariant', [...v.options].reverse().filter((o) => o !== 'Default Title').join(' · '));
    set('.sticky-atc__deal', deal && deal.tier ? deal.tier.title : '');
    set('#atcPrice', money(t.price));
    set('#atcCompare', t.compare > t.price ? money(t.compare) : '');
    const ok = s.available(v);
    set('#atcBtnMainLabel', ok ? `${this.el.dataset.label || 'Add to cart'} · ${money(t.price)}` : this.el.dataset.soldOut || 'Sold out');
    const img = /** @type {HTMLImageElement | null} */ (this.el.querySelector('#atcThumb img'));
    if (img && v.image) img.src = v.image;
    this.el.setAttribute('data-sticky-sold-out', String(!ok));
    this.el.querySelector('#atcBtnMain')?.classList.toggle('sticky-atc__btn--sold-out', !ok);
  }
}
/** @type {Sticky | null} */
let sticky = null;

// ------------------------------------------------------------------ boot
function boot() {
  for (const json of document.querySelectorAll('script[data-sb-gp-json]')) scopeOf(json);
  const bars = document.querySelectorAll('#stickyAtc[data-sb-gp-sticky]');
  bars.forEach((b, i) => {
    if (i > 0) b.remove(); // one bar per page (two product nodes may render it)
  });
  if (bars[0] && !sticky) {
    // fixed to the screen: out of the GemPages wrapper (its transforms and button resets)
    if (bars[0].parentElement !== document.body) document.body.append(bars[0]);
    sticky = new Sticky(/** @type {HTMLElement} */ (bars[0]));
  }
  // the URL's variant (?variant=) is already rendered server side; sync the client state
  for (const json of document.querySelectorAll('script[data-sb-gp-json]')) {
    const s = scopeOf(json);
    if (s && s.variant) s.setVariant(s.variant, 'init');
  }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
window.addEventListener('pageshow', (e) => {
  if (/** @type {PageTransitionEvent} */ (e).persisted) readCart();
});
